import React, { useEffect, useState } from 'react';
import { FunnelService } from '../../../../../services/applicant_tracking/analytics/FunnelService';
import {
  Funnel,
  FunnelStage,
} from '../../../../../entities/applicant_tracking/analytics/Funnel';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { TitledPanel } from '../TitledPanel';
import { InfoTooltip } from '../../../../../components/InfoTooltip';
import { CardTitle } from '../../../../../components/Card';
import { FilterState } from '../../Filters';
import { LabelledSelect } from '../../../../../components/LabelledSelect';
import { SelectOption } from '../../../../../components/Select';
import { buildPassThroughRateValue } from '../../../../../utils/applicant_tracking/analytics';
import { WidgetEmptyStateContent } from '../../../../survey_engine/WidgetEmptyStateContent';

// Fixed colour stops: gray → lavender → pink → yellow → orange → teal → green
// Interpolated dynamically based on the number of stages
const FUNNEL_COLOR_STOPS = [
  '#D5D7E0',
  '#9BA3D6',
  '#E07DA0',
  '#F5D880',
  '#F2B07E',
  '#6DD8B5',
  '#4CAF6A',
];

function interpolateColor(color1: string, color2: string, t: number): string {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);
  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, '0')}${g
    .toString(16)
    .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function getFunnelColor(index: number, total: number): string {
  if (total <= 1) return FUNNEL_COLOR_STOPS[0];
  const position = (index / (total - 1)) * (FUNNEL_COLOR_STOPS.length - 1);
  const lower = Math.floor(position);
  const upper = Math.min(lower + 1, FUNNEL_COLOR_STOPS.length - 1);
  return interpolateColor(
    FUNNEL_COLOR_STOPS[lower],
    FUNNEL_COLOR_STOPS[upper],
    position - lower,
  );
}

interface PropTypes {
  filterState?: FilterState;
}

export function FunnelWidget(props: PropTypes) {
  return (
    <TitledPanel
      title={
        <div className='d-flex align-items-center gap-2'>
          <CardTitle>Pass-Through Rate by Stage</CardTitle>
          <InfoTooltip>
            Percentage of candidates advancing from one stage to the next.
          </InfoTooltip>
        </div>
      }
      height='auto'
    >
      <FunnelContent filterState={props.filterState} />
    </TitledPanel>
  );
}

function FunnelContent(props: { filterState?: FilterState }) {
  const [data, setData] = useState<Funnel | null>(null);
  const [loading, setLoading] = useState(true);
  const [startStageId, setStartStageId] = useState<number | undefined>(
    undefined,
  );
  const [endStageId, setEndStageId] = useState<number | undefined>(undefined);
  const [stagesInitialized, setStagesInitialized] = useState(false);

  const filterStateJson = JSON.stringify(props.filterState);

  useEffect(() => {
    setStagesInitialized(false);
    setStartStageId(undefined);
    setEndStageId(undefined);
  }, [filterStateJson]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    FunnelService.show(
      props.filterState,
      controller.signal,
      startStageId,
      endStageId,
    )
      .then((result) => {
        setData(result);

        if (!stagesInitialized && result.stages.length > 0) {
          setStartStageId(result.stages[0].stageId);
          setEndStageId(result.stages[result.stages.length - 1].stageId);
          setStagesInitialized(true);
        }

        setLoading(false);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setLoading(false);
        throw e;
      });

    return () => {
      try {
        controller.abort();
      } catch (e) {
        if (e.name !== 'AbortError') throw e;
      }
    };
  }, [filterStateJson, startStageId, endStageId]);

  if (loading || !data) return <LoadingSpinner />;

  if (data.stages.length === 0) return <WidgetEmptyStateContent />;

  return (
    <div className='d-flex flex-column gap-3 h-100'>
      <StageSelectors
        stages={data.stages}
        startStageId={startStageId}
        endStageId={endStageId}
        onStartStageChange={setStartStageId}
        onEndStageChange={setEndStageId}
      />
      <FunnelChart stages={data.stages} />
    </div>
  );
}

function StageSelectors(props: {
  stages: FunnelStage[];
  startStageId?: number;
  endStageId?: number;
  onStartStageChange: (id: number) => void;
  onEndStageChange: (id: number) => void;
}) {
  const stageOptions: SelectOption[] = props.stages.map((stage) => ({
    value: String(stage.stageId),
    label: stage.stageName,
  }));

  const selectedStart = stageOptions.find(
    (opt) => opt.value === String(props.startStageId),
  );

  const selectedEnd = stageOptions.find(
    (opt) => opt.value === String(props.endStageId),
  );

  return (
    <div className='d-flex gap-3'>
      <LabelledSelect
        label='Start Stage'
        options={stageOptions}
        selected={selectedStart}
        isClearable={false}
        onChange={(option) => props.onStartStageChange(Number(option.value))}
        className='flex-fill'
      />
      <LabelledSelect
        label='End Stage'
        options={stageOptions}
        selected={selectedEnd}
        isClearable={false}
        onChange={(option) => props.onEndStageChange(Number(option.value))}
        className='flex-fill'
      />
    </div>
  );
}

interface TooltipState {
  x: number;
  y: number;
  stageIndex: number;
}

function FunnelChart(props: { stages: FunnelStage[] }) {
  const { stages } = props;
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const maxCount = stages.reduce(
    (max, s) => Math.max(max, s.candidateCount),
    0,
  );

  const svgWidth = 800;
  const svgHeight = 280;
  const labelHeight = 50;
  const chartHeight = svgHeight - labelHeight;
  const midY = chartHeight / 2;
  const stageCount = stages.length;
  const gap = 1;
  const totalGaps = (stageCount - 1) * gap;
  const stageWidth = (svgWidth - totalGaps) / stageCount;
  const minBarHeight = 6;
  const minRatio = 0.15;

  function barHeight(count: number): number {
    if (maxCount === 0 || count === 0) return minBarHeight;

    const ratio = count / maxCount;
    const smoothed = Math.sqrt(ratio);
    const scaled = minRatio + smoothed * (1 - minRatio);
    return Math.max(scaled * chartHeight, minBarHeight);
  }

  function handleMouseMove(
    e: React.MouseEvent<SVGPolygonElement>,
    stageIndex: number,
  ) {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      stageIndex,
    });
  }

  const tooltipStage = tooltip != null ? stages[tooltip.stageIndex] : null;
  const prevStage =
    tooltip != null && tooltip.stageIndex > 0
      ? stages[tooltip.stageIndex - 1]
      : null;

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', overflow: 'hidden', position: 'relative' }}
    >
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio='xMidYMid meet'
        style={{ width: '100%', height: 'auto' }}
      >
        {stages.map((stage, i) => {
          const x = i * (stageWidth + gap);
          const currentH = barHeight(stage.candidateCount);
          const nextStage = stages[i + 1];
          const nextH = nextStage
            ? barHeight(nextStage.candidateCount)
            : currentH * 0.5;
          const isLast = i === stageCount - 1;

          const leftTop = midY - currentH / 2;
          const leftBottom = midY + currentH / 2;
          const rightH = isLast ? 0 : nextH;
          const rightTop = midY - rightH / 2;
          const rightBottom = midY + rightH / 2;

          const color = getFunnelColor(i, stageCount);

          const points = [
            `${x},${leftTop}`,
            `${x + stageWidth},${rightTop}`,
            `${x + stageWidth},${rightBottom}`,
            `${x},${leftBottom}`,
          ].join(' ');

          const ptrText =
            stage.passThroughRate != null
              ? `${buildPassThroughRateValue(stage.passThroughRate)}%`
              : '';

          const labelX = x + stageWidth / 2;
          const labelY = chartHeight + 16;

          const boundaryX = x + stageWidth;
          const shapeBottomAtBoundary = Math.max(
            rightBottom,
            isLast ? midY : rightBottom,
          );

          return (
            <g key={stage.stageId}>
              <polygon
                points={points}
                fill={color}
                style={{ cursor: 'pointer' }}
                onMouseMove={(e) => handleMouseMove(e, i)}
                onMouseLeave={() => setTooltip(null)}
              />
              {!isLast && (
                <line
                  x1={boundaryX}
                  y1={shapeBottomAtBoundary}
                  x2={boundaryX}
                  y2={svgHeight}
                  stroke='#D1D5DB'
                  strokeWidth='1'
                  strokeDasharray='3,3'
                />
              )}
              <text
                x={labelX}
                y={labelY}
                textAnchor='middle'
                fontSize='8'
                fontWeight='600'
                fill='#1A1A2E'
              >
                {stage.stageName}
              </text>
              <text
                x={labelX}
                y={labelY + 12}
                textAnchor='middle'
                fontSize='7'
                fill='#6B7280'
              >
                {stage.candidateCount}
                {ptrText ? ` (${ptrText})` : ''}
              </text>
            </g>
          );
        })}
      </svg>
      {tooltip && tooltipStage && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%) translateY(-12px)',
            backgroundColor: '#2D3748',
            color: '#FFFFFF',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            lineHeight: '1.4',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: getFunnelColor(tooltip.stageIndex, stageCount),
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            {prevStage
              ? `${prevStage.stageName} → ${tooltipStage.stageName}`
              : tooltipStage.stageName}
          </div>
          {prevStage && (
            <div style={{ color: '#CBD5E0' }}>
              {tooltipStage.candidateCount}/{prevStage.candidateCount}{' '}
              candidates passed
            </div>
          )}
          {tooltipStage.passThroughRate != null && (
            <div style={{ color: '#CBD5E0' }}>
              PTR: {buildPassThroughRateValue(tooltipStage.passThroughRate)}%
            </div>
          )}
          {!prevStage && (
            <div style={{ color: '#CBD5E0' }}>
              {tooltipStage.candidateCount} candidates
            </div>
          )}
        </div>
      )}
    </div>
  );
}
